import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Mic, Package, X, Loader2, CheckCircle2, Sparkles, Grid3X3, Image as ImageIcon } from 'lucide-react';
import { compressProductImage } from '../utils/imageCompressor';

interface ProductRow {
  id: string;
  name: string;
  unit: string;
  costPrice?: number;
  sellingPrice?: number;
  stockQty?: number;
  category?: string;
  productImage?: string;
}

interface VoiceProductHologramProps {
  isOpen: boolean;
  onClose: () => void;
  storeApiKey: string;
  onProductSaved?: (product: any) => void;
  onSaveComplete?: () => void;
  shopType?: string;
}

const UNIT_ICONS: Record<string, string> = { Pieces: '📦', Kg: '⚖️', Grams: '⚖️', Liters: '💧', ml: '💧' };

export default function VoiceProductHologram({ isOpen, onClose, storeApiKey, onProductSaved, onSaveComplete, shopType }: VoiceProductHologramProps) {
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [justAdded, setJustAdded] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [status, setStatus] = useState<'listening' | 'saving' | 'saved' | 'idle'>('listening');
  
  // Keep the latest onSaveComplete in a ref to avoid stale closure issues
  const onSaveCompleteRef = useRef(onSaveComplete);
  onSaveCompleteRef.current = onSaveComplete;

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setProducts([]);
      setJustAdded(new Set());
      setSaving(false);
      setSaved(false);
      setSavedCount(0);
      setStatus('listening');
    }
  }, [isOpen]);

  // Listen for real-time grid updates from Luna (UPDATE_GRID)
  useEffect(() => {
    const handler = (e: Event) => {
      const { products: incoming } = (e as CustomEvent).detail || {};
      if (!Array.isArray(incoming)) return;

      // Find newly added rows by comparing IDs/names
      const existingIds = new Set(products.map(p => p.id));
      const newRows: ProductRow[] = [];

      const normalized = incoming.map((p: any, idx: number) => {
        const id = `${p.name || 'product'}_${idx}_${Date.now().toString().slice(-4)}`;
        if (!existingIds.has(id)) newRows.push({ id, ...p });
        return { id, ...p };
      });

      setProducts(normalized);

      // Flash pulse on new rows
      const newIds = new Set(newRows.map(r => r.id));
      if (newIds.size > 0) {
        setJustAdded(prev => new Set([...prev, ...newIds]));
        setTimeout(() => {
          setJustAdded(prev => { const n = new Set(prev); newIds.forEach(id => n.delete(id)); return n; });
        }, 1500);
      }
    };
    window.addEventListener('luna-live-grid-update', handler);
    return () => window.removeEventListener('luna-live-grid-update', handler);
  }, [products]);

  // Listen for bulk save command from Luna
  useEffect(() => {
    const handler = async () => {
      await doSaveAll();
    };
    window.addEventListener('luna-live-grid-save', handler);
    return () => window.removeEventListener('luna-live-grid-save', handler);
  }, [products, storeApiKey]);

  const doSaveAll = async () => {
    if (products.length === 0) return;
    
    // Auth: backend accepts EITHER a valid Cashier JWT OR a valid API Key.
    // Send the JWT if available (cashier mode), otherwise rely on the store API key (manager mode).
    const cashierToken = localStorage.getItem('kangavena:cashierToken');
    
    setSaving(true);
    setStatus('saving');
    try {
      const res = await fetch('/api/products/bulk', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-store-api-key': storeApiKey,
          ...(cashierToken ? { 'Authorization': `Bearer ${cashierToken}` } : {})
        },
        body: JSON.stringify({
          apiKey: storeApiKey,
          products: products.map(p => ({
            name: p.name?.trim(),
            unit: ['Pieces', 'Kg', 'Grams', 'Liters', 'ml'].includes(p.unit) ? p.unit : 'Pieces',
            cost: p.costPrice ?? null,
            selling: p.sellingPrice ?? 0,
            qty: p.stockQty ?? 0,
            category: p.category || 'Grocery',
            productImage: p.productImage || null,
          })),
        }),
      });
      // CRITICAL FIX: Explicitly handle HTTP errors (401, 413, 500) to stop infinite spinner
      if (!res.ok) {
        setStatus('listening');
        setSaving(false);
        const errorData = await res.json().catch(() => ({ error: res.statusText }));
        alert(`Save Failed: ${errorData.error || res.statusText} (HTTP ${res.status})`);
        return; // STOP EXECUTION HERE
      }
      const data = await res.json();
      if (data.success) {
        setSaved(true);
        setSavedCount(data.count || products.length);
        setStatus('saved');
        // STRICTLY trigger parent inventory re-fetch instead of optimistic local updates
        onSaveCompleteRef.current?.();
        setTimeout(() => { onClose(); }, 2500);
      } else {
        setStatus('listening');
        setSaving(false);
        alert(`Save failed: ${data.error || 'Unknown server error'}`);
      }
    } catch (err: any) {
      // CRITICAL FIX: Reset status on exception to stop infinite spinner
      setStatus('listening');
      setSaving(false);
      alert(`Network error: ${err?.message || 'Failed to connect to server'}`);
    }
  };

  // Manual row management
  const updateRow = (id: string, field: keyof ProductRow, value: any) => {
    setProducts(prev => prev.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  const removeRow = (id: string) => {
    setProducts(prev => prev.filter(p => p.id !== id));
  };

  const addEmptyRow = () => {
    const newRow: ProductRow = {
      id: `manual_${Date.now()}`,
      name: '',
      unit: 'Pieces',
      costPrice: undefined,
      sellingPrice: undefined,
      stockQty: undefined,
      category: 'Grocery',
    };
    setProducts(prev => [...prev, newRow]);
    setJustAdded(prev => new Set([...prev, newRow.id]));
    setTimeout(() => {
      setJustAdded(p => { const n = new Set(p); n.delete(newRow.id); return n; });
    }, 1500);
  };

  const rowPulse = (id: string) => justAdded.has(id)
    ? 'bg-cyan-500/15 border-cyan-400/50 shadow-[0_0_20px_rgba(6,182,212,0.3)]'
    : 'bg-white/[0.02] border-white/[0.04]';

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'radial-gradient(ellipse at center, rgba(6,182,212,0.08) 0%, rgba(0,0,0,0.9) 70%)' }}
      onClick={e => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      {/* Holographic scan lines overlay */}
      <div className="pointer-events-none fixed inset-0 z-[1]"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(6,182,212,0.03) 2px, rgba(6,182,212,0.03) 4px)',
          animation: 'scanlines 8s linear infinite',
        }} />

      <motion.div
        initial={{ scale: 0.85, y: 30 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.85, y: 30, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 200, damping: 20 }}
        className="relative z-10 w-full max-w-4xl max-h-[90vh] rounded-3xl overflow-hidden flex flex-col"
        style={{
          background: 'linear-gradient(135deg, rgba(6,182,212,0.08) 0%, rgba(15,20,40,0.95) 40%, rgba(10,14,26,0.98) 100%)',
          border: '1px solid rgba(6,182,212,0.25)',
          boxShadow: '0 0 100px rgba(6,182,212,0.15), 0 0 300px rgba(6,182,212,0.05), inset 0 1px 0 rgba(6,182,212,0.15)',
        }}
      >
        {/* Animated top edge glow */}
        <div className="absolute top-0 left-0 right-0 h-[2px]"
          style={{
            background: 'linear-gradient(90deg, transparent 0%, rgba(6,182,212,0.9) 30%, rgba(139,92,246,0.7) 70%, transparent 100%)',
            animation: 'shimmer 3s ease-in-out infinite',
          }} />

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-cyan-500/10">
          <div className="flex items-center gap-3">
            <motion.div
              animate={{ boxShadow: ['0 0 12px rgba(6,182,212,0.3)', '0 0 24px rgba(6,182,212,0.6)', '0 0 12px rgba(6,182,212,0.3)'] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="w-10 h-10 rounded-xl bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center"
            >
              {status === 'saved' ? <CheckCircle2 size={18} className="text-emerald-400" /> :
               status === 'saving' ? <Loader2 size={18} className="text-cyan-400 animate-spin" /> :
               <Grid3X3 size={18} className="text-cyan-400" />}
            </motion.div>
            <div>
              <h2 className="text-sm font-black text-white tracking-wide flex items-center gap-2">
                Live Data Grid
                <Sparkles size={12} className="text-cyan-400 animate-pulse" />
              </h2>
              <p className="text-[9px] text-cyan-400/60 font-mono">
                {status === 'listening' ? `● VOICE SYNC ACTIVE — ${products.length} row(s) in grid` :
                 status === 'saving' ? `● SAVING ${products.length} PRODUCTS...` :
                 status === 'saved' ? `✓ ${savedCount} PRODUCTS SAVED` : ''}
              </p>
            </div>
          </div>
          {/* Voice Ready Badge */}
          {status === 'listening' && (
            <motion.div
              animate={{ scale: [1, 1.1, 1], opacity: [0.8, 1, 0.8] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/30"
            >
              <Mic size={10} className="text-cyan-400" />
              <span className="text-[9px] font-bold text-cyan-300 uppercase tracking-wider">I'm Ready</span>
            </motion.div>
          )}
          <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/8 text-slate-600 hover:text-white transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Dictation hint */}
        {status === 'listening' && (
          <div className="px-5 py-2 bg-cyan-500/[0.04] border-b border-cyan-500/10">
            <p className="text-[10px] text-cyan-400/70 font-mono text-center">
              🎙️ <span className="text-cyan-300">Dictate products:</span>{shopType === 'AUTO_PARTS' ? ' "Toyota Prius Brake Pads 8500 rupees 5 items" • "NGK Spark Plug 1200 rupees 20 items"' : ' "Samba rice 130 rupees 50 kilos" • "Sugar 2kg cost 80 sell 100"'} • Say "save all" when done
            </p>
          </div>
        )}

        {saved ? (
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="flex flex-col items-center justify-center py-16 gap-4">
            <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 0.6, repeat: 2 }}>
              <CheckCircle2 size={64} className="text-emerald-400" />
            </motion.div>
            <p className="text-2xl font-black text-white">{savedCount} Products Saved!</p>
            <p className="text-[11px] text-emerald-400/80 font-mono">Inventory updated successfully</p>
          </motion.div>
        ) : (
          <>
            {/* Data Grid */}
            <div className="flex-1 overflow-auto px-5 py-3">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-[8px] text-cyan-400/50 uppercase tracking-[0.15em] font-mono border-b border-cyan-500/20">
                    <th className="text-left py-2 px-2 w-[5%]"></th>
                    <th className="text-left py-2 px-2 w-[8%]">Img</th>
                    <th className="text-left py-2 px-2 w-[24%]">Product Name</th>
                    <th className="text-left py-2 px-2 w-[9%]">Unit</th>
                    <th className="text-left py-2 px-2 w-[12%]">Cost</th>
                    <th className="text-left py-2 px-2 w-[12%]">Sell</th>
                    <th className="text-left py-2 px-2 w-[10%]">Stock</th>
                    <th className="text-left py-2 px-2 w-[14%]">Category</th>
                    <th className="text-center py-2 px-2 w-[6%]"></th>
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence>
                    {products.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="py-12 text-center">
                          <p className="text-slate-700 font-mono text-[11px]">
                            <Mic size={20} className="inline-block mb-2 opacity-40" /><br />
                            Waiting for voice input...<br />
                            <span className="text-[9px] opacity-60">{shopType === 'AUTO_PARTS' ? 'Say: "Toyota Prius Brake Pads, 8500 rupees, 5 items"' : 'Say: "Samba rice, 130 rupees, 50 kilos"'}</span>
                          </p>
                        </td>
                      </tr>
                    ) : (
                      products.map((p, idx) => (
                        <motion.tr
                          key={p.id}
                          initial={{ opacity: 0, x: -20, scale: 0.98 }}
                          animate={{ opacity: 1, x: 0, scale: 1 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ type: 'spring', stiffness: 300, damping: 25, delay: idx * 0.03 }}
                          className={`border-b border-white/[0.03] hover:bg-white/[0.02] ${rowPulse(p.id)} transition-all duration-700`}
                        >
                          <td className="py-1.5 px-2">
                            <span className="text-slate-600 font-mono text-[10px]">{idx + 1}</span>
                          </td>
                          <td className="py-1.5 px-2">
                            <div className="relative group">
                              {p.productImage ? (
                                <img src={p.productImage} alt="" className="w-10 h-10 object-cover rounded-lg border border-cyan-500/20" />
                              ) : (
                                <div className="w-10 h-10 rounded-lg border border-dashed border-white/10 flex items-center justify-center bg-white/[0.02] group-hover:border-cyan-500/30 transition-colors">
                                  <ImageIcon size={12} className="text-slate-600 group-hover:text-cyan-500 transition-colors" />
                                </div>
                              )}
                              <input
                                type="file"
                                accept="image/*"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    try {
                                      const compressed = await compressProductImage(file, 512, 512, 0.85);
                                      updateRow(p.id, 'productImage', compressed);
                                    } catch {
                                      const reader = new FileReader();
                                      reader.onload = (ev) => updateRow(p.id, 'productImage', ev.target?.result as string);
                                      reader.readAsDataURL(file);
                                    }
                                  }
                                }}
                                className="absolute inset-0 opacity-0 cursor-pointer"
                              />
                            </div>
                          </td>
                          <td className="py-1.5 px-2">
                            <input
                              value={p.name || ''}
                              onChange={e => updateRow(p.id, 'name', e.target.value)}
                              placeholder="Product..."
                              className="w-full bg-transparent text-white text-[11px] font-mono font-bold placeholder:text-slate-800 outline-none"
                            />
                          </td>
                          <td className="py-1.5 px-2">
                            <select
                              value={p.unit || 'Pieces'}
                              onChange={e => updateRow(p.id, 'unit', e.target.value)}
                              className="w-full bg-transparent text-cyan-300 text-[10px] font-mono outline-none cursor-pointer"
                            >
                              {Object.entries(UNIT_ICONS).map(([u, icon]) => (
                                <option key={u} value={u} className="bg-[#0a0e1a]">
                                  {icon} {u === 'Pieces' ? 'Pcs' : u === 'Grams' ? 'g' : u === 'Liters' ? 'L' : u}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="py-1.5 px-2">
                            <input
                              type="number"
                              value={p.costPrice ?? ''}
                              onChange={e => updateRow(p.id, 'costPrice', e.target.value ? parseFloat(e.target.value) : undefined)}
                              placeholder="—"
                              className="w-full bg-transparent text-orange-300 text-[11px] font-mono placeholder:text-slate-800 outline-none"
                            />
                          </td>
                          <td className="py-1.5 px-2">
                            <input
                              type="number"
                              value={p.sellingPrice ?? ''}
                              onChange={e => updateRow(p.id, 'sellingPrice', e.target.value ? parseFloat(e.target.value) : undefined)}
                              placeholder="—"
                              className="w-full bg-transparent text-emerald-300 text-[11px] font-mono placeholder:text-slate-800 outline-none"
                            />
                          </td>
                          <td className="py-1.5 px-2">
                            <input
                              type="number"
                              value={p.stockQty ?? ''}
                              onChange={e => updateRow(p.id, 'stockQty', e.target.value ? parseFloat(e.target.value) : undefined)}
                              placeholder="0"
                              className="w-full bg-transparent text-white text-[11px] font-mono placeholder:text-slate-800 outline-none"
                            />
                          </td>
                          <td className="py-1.5 px-2">
                            <input
                              value={p.category || ''}
                              onChange={e => updateRow(p.id, 'category', e.target.value)}
                              placeholder="Grocery"
                              className="w-full bg-transparent text-violet-300 text-[11px] font-mono placeholder:text-slate-800 outline-none"
                            />
                          </td>
                          <td className="py-1.5 px-2 text-center">
                            <button
                              onClick={() => removeRow(p.id)}
                              className="p-1 rounded hover:bg-red-500/20 text-slate-600 hover:text-red-400 transition-colors"
                            >
                              <X size={12} />
                            </button>
                          </td>
                        </motion.tr>
                      ))
                    )}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>

            {/* Footer controls */}
            <div className="flex items-center justify-between px-5 py-4 border-t border-cyan-500/10 bg-black/20">
              <div className="flex items-center gap-3">
                <button
                  onClick={addEmptyRow}
                  className="px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-slate-500 hover:text-white hover:border-white/20 text-[10px] font-bold font-mono transition-all"
                >
                  + Add Row
                </button>
                <span className="text-[9px] text-slate-600 font-mono">
                  {products.length} item{products.length !== 1 ? 's' : ''} ready
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-slate-500 hover:text-white text-[10px] font-bold transition-all"
                >
                  Discard
                </button>
                <motion.button
                  whileTap={{ scale: 0.96 }}
                  onClick={doSaveAll}
                  disabled={saving || products.length === 0}
                  className="flex items-center gap-1.5 px-5 py-2 rounded-lg bg-gradient-to-r from-cyan-500 to-violet-500 text-black text-[10px] font-black uppercase tracking-wider shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:shadow-[0_0_40px_rgba(6,182,212,0.5)] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Package size={14} />}
                  {saving ? 'Saving...' : `Save All (${products.length})`}
                </motion.button>
              </div>
            </div>
          </>
        )}
      </motion.div>

      {/* Global keyframes */}
      <style>{`
        @keyframes scanlines {
          0% { background-position: 0 0; }
          100% { background-position: 0 200px; }
        }
        @keyframes shimmer {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
      `}</style>
    </motion.div>
  );
}
